import { defineStore, storeToRefs } from "pinia";
import type { Address } from "@/services/addresses/addressTypes";
import type { UserBankInfo } from "@/services/user/bankInfo/bankInfoTypes";
import { formatIban, normalizeIban } from "@/services/user/bankInfo/bankInfoHelpers";
import { assertUserFlagsAreUnique } from "@/services/user/settings/validators";
import type { UserSettings } from "@/services/user/settings/types";
import type { UserFlag } from "@/services/user/settings/flags/types";

const useUserSettingsPiniaStore = defineStore("userSettings", () => {
  const userSettings = ref<UserSettings>();
  const bankInfo = ref<UserBankInfo>();
  const address = ref<Address>();
  const isEmailVerified = ref<boolean>();

  const userHasFlag = (flag: UserFlag): boolean => {
    return userSettings.value?.flags.includes(flag) ?? false;
  };

  const removeUserFlag = (flag: UserFlag): void => {
    userSettings.value?.flags.splice(userSettings.value?.flags.indexOf(flag), 1);
  };

  watch(userSettings, (newSetting) => {
    if (newSetting !== undefined) {
      assertUserFlagsAreUnique(newSetting.flags);
    }
  });

  const formattedPrimaryAccountNumber = computed(() => {
    const bank = bankInfo.value;
    if (bank === undefined) {
      return undefined;
    } else if (bank.iban !== "") {
      return formatIban(normalizeIban(bank.iban));
    } else if (bank.accountNumber !== "") {
      return bank.accountNumber;
    } else {
      return undefined;
    }
  });

  const nickname = computed(() => userSettings.value?.nickname);
  const absenceUntil = computed<Date | null>({
    get: () => userSettings.value?.absenceUntil ?? null,
    set: (value) => {
      if (userSettings.value !== undefined) {
        userSettings.value.absenceUntil = value;
      }
    },
  });

  return {
    userSettings,
    bankInfo,
    address,
    isEmailVerified,
    nickname,
    absenceUntil,
    formattedPrimaryAccountNumber,
    userHasFlag,
    removeUserFlag,
  };
});

export function useUserSettings() {
  const store = useUserSettingsPiniaStore();
  const { userSettings, bankInfo, address, isEmailVerified, nickname, absenceUntil, formattedPrimaryAccountNumber } = storeToRefs(store);

  return {
    userSettings,
    bankInfo,
    address,
    isEmailVerified,
    nickname,
    absenceUntil,
    formattedPrimaryAccountNumber,
    userHasFlag: store.userHasFlag,
    removeUserFlag: store.removeUserFlag,
  };
}
